import { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { useAuth } from "@/context/AuthContext";
import BackButton from "@/components/BackButton";
export default function Auth() {
  const [params] = useSearchParams();
  const initialMode = params.get("mode") === "login" ? "login" : "register";
  const [mode, setMode] = useState(initialMode);
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [name, setName] = useState("");
  const [ageConfirmed, setAgeConfirmed] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const { login, register, error, setError, user } = useAuth();
  const nav = useNavigate();

  useEffect(() => { if (user && user.id) nav("/library"); }, [user, nav]);
  useEffect(() => { setError(""); setAgeConfirmed(false); }, [mode, setError]);

  const submit = async (e) => {
    e.preventDefault();
    if (mode === "register" && !ageConfirmed) {
      setError("Please confirm you're at least 13 years old.");
      return;
    }
    setSubmitting(true);
    const ok = mode === "login" ? await login(email, password) : await register(email, password, name);
    setSubmitting(false);
    if (ok) nav("/library");
  };

  return (
    <main data-testid="auth-page" className="max-w-md mx-auto px-6 py-16 md:py-24">
      <BackButton to="/" label="Back to home" testid="auth-back-btn" />
      <p className="overline-label mt-6">{mode === "login" ? "Welcome back" : "Get started"}</p>
      <h1 className="mt-3 font-serif text-4xl md:text-5xl tracking-tight">
        {mode === "login" ? "Sign in to keep practicing." : "Create your practice room."}
      </h1>

      <form onSubmit={submit} className="mt-9 space-y-5" data-testid="auth-form">
        {mode === "register" && (
          <div>
            <Label htmlFor="name" className="overline-label">Name</Label>
            <Input
              id="name" data-testid="auth-name-input"
              value={name} onChange={(e) => setName(e.target.value)}
              placeholder="Your name" className="mt-2 h-11"
            />
          </div>
        )}
        <div>
          <Label htmlFor="email" className="overline-label">Email</Label>
          <Input
            id="email" type="email" required data-testid="auth-email-input"
            value={email} onChange={(e) => setEmail(e.target.value)}
            placeholder="you@example.com" className="mt-2 h-11"
          />
        </div>
        <div>
          <Label htmlFor="password" className="overline-label">Password</Label>
          <Input
            id="password" type="password" required data-testid="auth-password-input"
            value={password} onChange={(e) => setPassword(e.target.value)}
            placeholder="At least 6 characters" className="mt-2 h-11" minLength={6}
          />
        </div>

        {error && (
          <p data-testid="auth-error" className="text-sm text-destructive">{error}</p>
        )}

        {mode === "register" && (
          <label className="flex items-start gap-2.5 text-sm text-foreground/90 cursor-pointer select-none"
                 data-testid="auth-age-label">
            <input
              type="checkbox"
              checked={ageConfirmed}
              onChange={(e) => setAgeConfirmed(e.target.checked)}
              className="mt-1 w-4 h-4 accent-primary shrink-0"
              data-testid="auth-age-checkbox"
              required
            />
            <span>I confirm I'm at least 13 years old.</span>
          </label>
        )}

        <Button
          type="submit"
          disabled={submitting || (mode === "register" && !ageConfirmed)}
          data-testid="auth-submit-btn"
          className="w-full h-12 rounded-full text-base"
        >
          {submitting ? "One moment…" : mode === "login" ? "Sign in" : "Create account"}
        </Button>

        {mode === "register" && (
          <p className="text-xs text-muted-foreground text-center leading-relaxed" data-testid="auth-legal-disclaimer">
            By signing up you agree to our{" "}
            <Link to="/terms" className="underline text-foreground hover:text-primary" data-testid="auth-terms-link">
              Terms of Service
            </Link>{" "}
            and{" "}
            <Link to="/privacy" className="underline text-foreground hover:text-primary" data-testid="auth-privacy-link">
              Privacy Policy
            </Link>.
          </p>
        )}
      </form>

      <p className="mt-8 text-sm text-muted-foreground">
        {mode === "login" ? "New to Lyric Lock?" : "Already have an account?"}{" "}
        <button
          data-testid="auth-toggle-mode"
          type="button"
          className="underline text-foreground hover:text-primary"
          onClick={() => setMode(mode === "login" ? "register" : "login")}
        >
          {mode === "login" ? "Create an account" : "Sign in"}
        </button>
      </p>
      <p className="mt-3 text-sm">
        <Link to="/" className="text-muted-foreground hover:text-foreground">Back to home</Link>
      </p>
    </main>
  );
}
